import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, TrendingUp, Users, CreditCard, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [periodDays, setPeriodDays] = useState('30');
  const [attendanceByDiscipline, setAttendanceByDiscipline] = useState([]);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [revenue, setRevenue] = useState({ total: 0, byMonth: [] });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, periodDays]);
  
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };
  
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      
      const [disciplineRes, trendRes, statusRes, revenueRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/attendance-by-discipline?days=${periodDays}${branchParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/attendance-trend?days=${periodDays}${branchParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/payment-status?${branchParam.slice(1)}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/revenue?months=6${branchParam}`, { credentials: 'include' })
      ]);
      
      if (disciplineRes.ok) {
        const data = await disciplineRes.json();
        setAttendanceByDiscipline(data.by_discipline);
      }
      
      if (trendRes.ok) {
        const data = await trendRes.json();
        // Format dates for display
        const formattedTrend = data.trend.map(item => ({
          ...item,
          date: new Date(item.date).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })
        }));
        setAttendanceTrend(formattedTrend);
      }
      
      if (statusRes.ok) {
        const data = await statusRes.json();
        setPaymentStatus(data.status);
      }
      
      if (revenueRes.ok) {
        const data = await revenueRes.json();
        const formattedRevenue = data.by_month.map(item => ({
          ...item,
          month: new Date(item.month + '-01').toLocaleDateString('es-BO', { month: 'short', year: '2-digit' })
        }));
        setRevenue({ total: data.total_revenue, byMonth: formattedRevenue });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoading(false);
    }
  };
  
  const COLORS = ['#E50914', '#22c55e', '#3b82f6', '#eab308', '#a855f7', '#ec4899'];
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-heading text-white uppercase">ANALYTICS</h1>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto p-4">
        <Card className="bg-zinc-950 border-zinc-800 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 uppercase mb-2 block">Sucursal</label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="branch-filter">
                  <SelectValue placeholder="Todas las sucursales" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.branch_id} value={branch.branch_id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-zinc-400 uppercase mb-2 block">Período</label>
              <Select value={periodDays} onValueChange={setPeriodDays}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Charts Grid */}
      <div className="max-w-7xl mx-auto p-4 pb-20 space-y-6">
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando datos...</div>
        ) : (
          <>
            {/* Row 1: Attendance by Discipline & Payment Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Attendance by Discipline */}
              <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="discipline-chart">
                <div className="flex items-center space-x-2 mb-4">
                  <Users className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-heading text-white uppercase">Asistencia por Disciplina</h3>
                </div>
                {attendanceByDiscipline.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-zinc-500">
                    No hay datos para mostrar
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={attendanceByDiscipline} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis type="number" stroke="#71717a" />
                      <YAxis dataKey="name" type="category" stroke="#71717a" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Bar dataKey="value" fill="#E50914" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
              
              {/* Payment Status Pie Chart */}
              <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="payment-chart">
                <div className="flex items-center space-x-2 mb-4">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-heading text-white uppercase">Estado de Pagos</h3>
                </div>
                {paymentStatus.length === 0 || paymentStatus.every(s => s.value === 0) ? (
                  <div className="h-64 flex items-center justify-center text-zinc-500">
                    No hay datos para mostrar
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={paymentStatus}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                        labelLine={false}
                      >
                        {paymentStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </div>
            
            {/* Row 2: Attendance Trend */}
            <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="trend-chart">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-heading text-white uppercase">Tendencia de Asistencia</h3>
              </div>
              {attendanceTrend.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-zinc-500">
                  No hay datos para mostrar
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attendanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#71717a" 
                      tick={{ fontSize: 10 }}
                      interval={Math.floor(attendanceTrend.length / 10)}
                    />
                    <YAxis stroke="#71717a" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#E50914" 
                      strokeWidth={2}
                      dot={{ fill: '#E50914', strokeWidth: 0, r: 3 }}
                      activeDot={{ r: 5, fill: '#fff', stroke: '#E50914', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
            
            {/* Row 3: Revenue */}
            <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="revenue-chart">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-heading text-white uppercase">Ingresos (Últimos 6 meses)</h3>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-400">Total</p>
                  <p className="text-2xl font-bold text-green-500">Bs {revenue.total.toLocaleString()}</p>
                </div>
              </div>
              {revenue.byMonth.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-zinc-500">
                  No hay datos para mostrar
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={revenue.byMonth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="month" stroke="#71717a" />
                    <YAxis stroke="#71717a" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`Bs ${value.toLocaleString()}`, 'Ingresos']}
                    />
                    <Bar dataKey="amount" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
